import type { PlainDateString, PlainDate } from "@sa-nu/shared-utils/date";
import { clsx } from "clsx";
import type { ComponentProps } from "react";
import React from "react";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import LoadingDots from "../../../ui/loading-dots";
import { useVirtualization } from "../_hooks/use-virtualization";
import { isCICOStayNight } from "../_utils/cico";
import type { DateCellDateType } from "../date-navigation/date-cell";
import {
  reservationCalendarGridColumnStyles,
  type AvailabilityCellClickHandler,
  type CICOSelection,
  type ReservationCalendarNavigationSize,
  type SelectedAccommodationReservation,
} from "../reservation-calendar";

import AccommodationHeaderCell from "./accommodation-header-cell";
import AvailabilityCell from "./cell/availability-cell";
import { AvailabilityCellFrame } from "./cell/availability-cell-frame";

const Fragment = graphql(`
  fragment AccommodationsOnCalendar on Accommodation {
    id
    name
    ...AccommodationOnHeaderCell
    ...AccommodationOnAvailabilityCell
    availabilities(
      firstNight: $firstNight
      lastNight: $lastNight
      provider: $provider
      occupancyLimitTarget: $occupancyLimitTarget
      internal: $internal
    ) @include(if: $withDeferred) {
      date
      isAvailable
      ...AvailabilityOnAvailabilityCell
    }
  }
`);

type Props = {
  accommodationsData: FragmentType<typeof Fragment>[];
  onAvailabilityCellClick: AvailabilityCellClickHandler;
  selected: SelectedAccommodationReservation | null;
  originalReservationData: ComponentProps<
    typeof AvailabilityCell
  >["originalReservationData"];
  getDateType: (dateString: PlainDateString | PlainDate) => DateCellDateType;
  navigationSize: ReservationCalendarNavigationSize;
  cico: CICOSelection | null;
  isInternal: boolean;
  isSampleMode: boolean;
  loadingAvailability?: boolean;
  onAccommodationClick?: (accommodationId: string) => void;
};

export function Accommodations({
  accommodationsData,
  onAvailabilityCellClick,
  selected,
  originalReservationData,
  getDateType,
  cico,
  navigationSize,
  isInternal,
  isSampleMode,
  loadingAvailability = false,
  onAccommodationClick,
}: Props) {
  const accommodations = useFragment(Fragment, accommodationsData);
  const { ref, shouldVirtualize } = useVirtualization();

  return (
    <div ref={ref} className="relative">
      <div
        className={clsx(
          "border-t-swhite *:border-b-sbeige grid auto-rows-[48px] border-t *:border-b md:auto-rows-[52px]",
          reservationCalendarGridColumnStyles[navigationSize].columns,
        )}
      >
        {accommodations.map((accommodation) => (
          <React.Fragment key={accommodation.id}>
            <AccommodationHeaderCell
              accommodationId={accommodation.id}
              accommodationData={accommodation}
              onAccommodationClick={onAccommodationClick}
            />
            {!shouldVirtualize &&
            accommodation.availabilities &&
            !loadingAvailability ? (
              accommodation.availabilities.map((availability, index) => (
                <AvailabilityCellFrame
                  key={index}
                  dateType={getDateType(availability.date)}
                  isInternal={isInternal}
                  isCICOHighlight={isCICOStayNight(availability.date, cico)}
                >
                  <AvailabilityCell
                    availabilityData={availability}
                    availabilitiesData={accommodation.availabilities!}
                    accommodationData={accommodation}
                    selected={
                      accommodation.id === selected?.accommodationId
                        ? selected
                        : null
                    }
                    originalReservationData={originalReservationData}
                    isSampleMode={isSampleMode}
                    onClick={onAvailabilityCellClick}
                  />
                </AvailabilityCellFrame>
              ))
            ) : !shouldVirtualize && loadingAvailability ? (
              <div
                className={clsx(
                  "flex items-center justify-center",
                  reservationCalendarGridColumnStyles[navigationSize].span,
                )}
              >
                <LoadingDots size="relaxed" />
              </div>
            ) : (
              <div
                className={clsx(
                  "border-r-slightbeige-116 border-r",
                  reservationCalendarGridColumnStyles[navigationSize].span,
                )}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>
      {/* グリッド活用した表現例
      https://sa-nu.slack.com/archives/C01AS7YA352/p1720507971534699?thread_ts=1718239482.863469&cid=C01AS7YA352
      ※ 親divにpointer-events-none、子にpointer-events-autoが必須 */}
      {/* <div className="pointer-events-none absolute left-0 top-0 grid w-full auto-rows-[48px] grid-cols-[68px_repeat(14,1fr)]">
      <div className="pointer-events-auto col-span-5 col-start-3 row-span-3 row-start-2 flex w-full items-center justify-center bg-red-500 font-bold text-white">
        メンテナンス中
      </div>
      <div className="pointer-events-auto col-span-5 col-start-2 row-span-6 row-start-7 flex w-full items-center justify-center bg-blue-500 font-bold text-white">
        開業準備中
      </div>
    </div> */}
    </div>
  );
}
